import React, { useEffect, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { backfillAssignedVetEmails } from './utils/patientStorage';
import { supabase } from './lib/supabaseClient';
import {
  getInitialAuthHash,
  isRecoveryOrAuthErrorHash,
} from './utils/authRecoveryHash';
import Landing from './pages/marketing/Landing';
import Products from './pages/marketing/Products';
import BioPly from './pages/marketing/BioPly';
import ProductQentorosPlatform from './pages/marketing/ProductQentorosPlatform';
import Contact from './pages/marketing/Contact';
import Team from './pages/marketing/Team';
import TeamMember from './pages/marketing/TeamMember';
import News from './pages/marketing/News';
import NewsArticle from './pages/marketing/NewsArticle';
import AdminRoute from './components/AdminRoute/AdminRoute';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import ShopGate from './components/ShopRoute/ShopGate';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider } from './contexts/AuthContext';

// The public marketing pages above are imported eagerly: they're the prerendered
// SSG routes (see src/entry-server.jsx), so their code has to be in the entry
// chunk to hydrate the static HTML without a second round trip.
//
// Everything below is behind auth, a form submission, or a login — none of it is
// reachable on a first marketing pageview, and statically importing it put the
// whole clinical-trial app in the entry chunk. On Lighthouse's throttled mobile
// connection those unused bytes competed for bandwidth with the render-blocking
// CSS and the LCP hero, which is what made FCP/LCP slow (main-thread time was
// never the bottleneck — TBT was already ~100ms). Splitting them out cut the
// entry chunk from 428 KB to 283 KB gzip and the entry CSS from 42 KB to 19 KB.
// Unapproved draft page: reachable by URL only. It's deliberately NOT linked
// from the nav, the footer, the sitemap, or the prerender list, and it renders
// a noindex tag — so it stays lazy rather than joining the SSG entry chunk.
// Launch checklist (including the X-Robots-Tag header in vercel.json that MUST
// be removed): docs/HORSE_OWNERS_PAGE.md
const HorseOwners = React.lazy(() => import('./pages/marketing/HorseOwners'));
const Login = React.lazy(() => import('./components/Login/Login'));
const CustomerEnrollment = React.lazy(() => import('./pages/marketing/CustomerEnrollment'));
const ResetPassword = React.lazy(() => import('./pages/auth/ResetPassword'));
const Dashboard = React.lazy(() => import('./components/Dashboard/Dashboard'));
const DashboardHome = React.lazy(() => import('./pages/app/DashboardHome'));
const Trials = React.lazy(() => import('./pages/app/Trials'));
const Patients = React.lazy(() => import('./pages/app/Patients'));
const PatientDetail = React.lazy(() => import('./pages/app/PatientDetail'));
const Forms = React.lazy(() => import('./pages/app/Forms'));
const Settings = React.lazy(() => import('./pages/app/Settings'));
const Support = React.lazy(() => import('./pages/app/Support'));
const Deadlines = React.lazy(() => import('./pages/app/Deadlines'));
const Activity = React.lazy(() => import('./pages/app/Activity'));
const Notifications = React.lazy(() => import('./pages/app/Notifications'));
const PatientOnboarding = React.lazy(() => import('./components/PatientOnboarding/PatientOnboarding'));
const PatientTreatment = React.lazy(() => import('./components/PatientTreatment/PatientTreatment'));
const PatientFollowUps = React.lazy(() => import('./components/PatientFollowUps/PatientFollowUps'));
const PatientSurvivalDischarge = React.lazy(() =>
  import('./components/PatientSurvivalDischarge/PatientSurvivalDischarge')
);
const AuditLog = React.lazy(() => import('./pages/app/AuditLog'));
const Analytics = React.lazy(() => import('./pages/app/Analytics'));
const AnalyticsReport = React.lazy(() => import('./pages/app/AnalyticsReport'));
const AccessRequests = React.lazy(() => import('./pages/app/AccessRequests'));
const EnrollmentDecision = React.lazy(() => import('./pages/app/EnrollmentDecision'));
const CustomerProfile = React.lazy(() => import('./pages/app/CustomerProfile'));
const Orders = React.lazy(() => import('./pages/app/Orders'));
const MyOrders = React.lazy(() => import('./pages/app/MyOrders'));
const OrderDetail = React.lazy(() => import('./pages/app/OrderDetail'));
const OrderDocument = React.lazy(() => import('./pages/app/OrderDocument'));
const Shop = React.lazy(() => import('./pages/app/Shop'));
const Product = React.lazy(() => import('./pages/app/Product'));
const Cart = React.lazy(() => import('./pages/app/Cart'));
const Checkout = React.lazy(() => import('./pages/app/Checkout'));
const SupportRequests = React.lazy(() => import('./pages/app/SupportRequests'));
const Leads = React.lazy(() => import('./pages/app/Leads'));
const Posts = React.lazy(() => import('./pages/app/Posts'));
const PostEditor = React.lazy(() => import('./pages/app/PostEditor'));

// Routes password-recovery / set-password links to /reset-password no matter
// where they land, so a link never dead-ends on the homepage.
//  - A valid link establishes a temporary session and fires PASSWORD_RECOVERY;
//    we forward to the reset page regardless of the route it opened on.
//  - An expired/used/old link can't establish a session, so Supabase bounces it
//    to the Site URL (the homepage) with an error in the URL hash. We detect
//    that hash (captured at app load before the client strips it) and forward
//    to /reset-password, where the "invalid or expired" notice explains it.
function AuthRecoveryRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        navigate('/reset-password', { replace: true });
      }
    });
    return () => listener?.subscription?.unsubscribe?.();
  }, [navigate]);

  useEffect(() => {
    if (location.pathname === '/reset-password') return;
    const hash = getInitialAuthHash();
    if (isRecoveryOrAuthErrorHash(hash)) {
      navigate(`/reset-password${hash}`, { replace: true });
    }
    // Runs once on initial load; the captured hash is a one-time signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

function App() {
  useEffect(() => {
    // Self-heal legacy patients created before `assignedVetEmail` existed so
    // they no longer show as Unassigned in the audit log.
    backfillAssignedVetEmails();
    const onAuth = () => backfillAssignedVetEmails();
    window.addEventListener('currentUserChanged', onAuth);
    return () => window.removeEventListener('currentUserChanged', onAuth);
  }, []);

  return (
    <AuthProvider>
      {/* reducedMotion="user" makes every framer-motion animation honor the
          OS "reduce motion" setting (WCAG 2.3.3); CSS keyframes already do. */}
      <MotionConfig reducedMotion="user">
      <Router>
        <ScrollToTop />
        <AuthRecoveryRedirect />
        {/* fallback={null} rather than a spinner: the eagerly-imported marketing
            routes never suspend, so this only covers the brief chunk fetch on
            the authenticated routes, which render their own loading states once
            mounted. A spinner here would flash on every dashboard navigation. */}
        <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/bio-ply" element={<BioPly />} />
          <Route path="/products/qentoros-platform" element={<ProductQentorosPlatform />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about/team" element={<Team />} />
          <Route path="/about/team/:memberId" element={<TeamMember />} />
          <Route path="/news" element={<News />} />
          <Route path="/news/:slug" element={<NewsArticle />} />
          <Route path="/for-horse-owners" element={<HorseOwners />} />
          {/* The single-page order form has been replaced by the in-dashboard
              storefront (Shop → Cart → Checkout). Keep the old path working by
              redirecting into the shop; /dashboard is behind ProtectedRoute, so
              an unauthenticated hit still routes to login from there. */}
          <Route path="/order" element={<Navigate to="/dashboard/shop" replace />} />
          <Route path="/enroll" element={<CustomerEnrollment />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* The printable order confirmation. Declared outside the dashboard
              shell so it renders as a bare sheet with no sidebar or header, but
              kept under /dashboard/my-orders/ so ProtectedRoute's customer
              allowlist still lets a shop-only clinic reach its own order. */}
          <Route
            path="/dashboard/my-orders/:id/document"
            element={
              <ProtectedRoute>
                <OrderDocument />
              </ProtectedRoute>
            }
          />
          {/* The printable analytics report. Declared outside the dashboard
              shell for the same reason as the order document above — it prints
              as a bare sheet — but kept under /dashboard/analytics/ so it sits
              behind the same admin gate as the screen it comes from. */}
          <Route
            path="/dashboard/analytics/report"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AnalyticsReport />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          >
          <Route index element={<DashboardHome />} />
          {/* Website analytics. Admin-only: this is business performance data,
              not something a clinic using the portal has any claim on. */}
          <Route
            path="analytics"
            element={
              <AdminRoute>
                <Analytics />
              </AdminRoute>
            }
          />
          <Route path="trials" element={<Trials />} />
          <Route path="patients" element={<Patients />} />
          <Route path="patients/:patientId" element={<PatientDetail />} />
          <Route path="patients/:patientId/onboarding" element={<PatientOnboarding />} />
          <Route path="patients/:patientId/treatment" element={<PatientTreatment />} />
          <Route path="patients/:patientId/followups" element={<PatientFollowUps />} />
          <Route path="patients/:patientId/survival-discharge" element={<PatientSurvivalDischarge />} />
          {/* BIO-PLY storefront (gated to shop-access users). */}
          <Route
            path="shop"
            element={
              <ShopGate>
                <Shop />
              </ShopGate>
            }
          />
          <Route
            path="shop/bio-ply"
            element={
              <ShopGate>
                <Product />
              </ShopGate>
            }
          />
          <Route
            path="cart"
            element={
              <ShopGate>
                <Cart />
              </ShopGate>
            }
          />
          <Route
            path="checkout"
            element={
              <ShopGate>
                <Checkout />
              </ShopGate>
            }
          />
          <Route path="my-orders" element={<MyOrders />} />
          <Route path="my-orders/:id" element={<OrderDetail />} />
          <Route path="deadlines" element={<Deadlines />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="activity" element={<Activity />} />
          <Route path="forms" element={<Forms />} />
          <Route
            path="audit"
            element={
              <AdminRoute>
                <AuditLog />
              </AdminRoute>
            }
          />
          <Route
            path="access"
            element={
              <AdminRoute>
                <AccessRequests />
              </AdminRoute>
            }
          />
          <Route
            path="access/decision"
            element={
              <AdminRoute>
                <EnrollmentDecision />
              </AdminRoute>
            }
          />
          {/* Full-page Customer 360. `:id` is a profile id; the literal
              "email" segment plus ?email= reaches an email-only customer. */}
          <Route
            path="access/users/:id"
            element={
              <AdminRoute>
                <CustomerProfile />
              </AdminRoute>
            }
          />
          <Route
            path="orders"
            element={
              <AdminRoute>
                <Orders />
              </AdminRoute>
            }
          />
          <Route
            path="support-requests"
            element={
              <AdminRoute>
                <SupportRequests />
              </AdminRoute>
            }
          />
          <Route
            path="leads"
            element={
              <AdminRoute>
                <Leads />
              </AdminRoute>
            }
          />
          {/* News CMS: the posts behind /news and the homepage news strip. */}
          <Route
            path="posts"
            element={
              <AdminRoute>
                <Posts />
              </AdminRoute>
            }
          />
          <Route
            path="posts/:id"
            element={
              <AdminRoute>
                <PostEditor />
              </AdminRoute>
            }
          />
          <Route path="settings" element={<Settings />} />
          <Route path="support" element={<Support />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </Router>
      </MotionConfig>
    </AuthProvider>
  );
}

export default App;
